<template>
  <footer class="relative bg-background-light border-t border-background-lighter mt-auto overflow-hidden">
    <!-- Decorative Elements -->
    <div class="absolute top-0 left-0 w-full overflow-hidden h-10">
      <div class="absolute top-0 left-0 w-full h-2 bg-primary opacity-10"></div>
      <div class="absolute top-0 left-0 w-20 h-2 bg-primary"></div>
      <div class="absolute top-0 left-1/3 w-12 h-2 bg-primary"></div>
      <div class="absolute top-0 right-1/4 w-16 h-2 bg-primary"></div>
    </div>

    <!-- Background Graphics -->
    <div class="absolute top-0 right-0 opacity-5 pointer-events-none">
      <svg width="300" height="300" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <path d="M50,10 L90,50 L50,90 L10,50 Z" fill="currentColor" class="text-primary"></path>
      </svg>
    </div>
    <div class="absolute bottom-0 left-0 opacity-5 pointer-events-none">
      <svg width="200" height="200" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="40" fill="currentColor" class="text-primary"></circle>
      </svg>
    </div>

    <!-- Main Footer Content -->
    <div class="container mx-auto px-4 py-12 relative z-10">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-12">
        <!-- Company Info -->
        <div class="md:col-span-4">
          <div class="flex items-center mb-5">
            <div class="w-10 h-10 bg-primary rounded-lg flex items-center justify-center shadow-lg">
              <img src="/logo.png" alt="Eskrag" class="h-6 w-6" @error="handleImageError">
            </div>
            <h3 class="text-primary font-bold text-2xl ml-3">Eskrag</h3>
          </div>

          <p class="text-text-secondary mb-5 max-w-md">
            Your one-stop gaming shop for digital content, gift cards, and subscriptions. We're committed to providing gamers worldwide with instant access to premium gaming experiences.
          </p>

          <div class="mt-6">
            <p class="font-medium text-lg mb-3 text-text">
              <span class="text-primary font-bold">Game On, </span>Level Up!
            </p>
            <p class="text-text-secondary text-sm">
              Our mission is to make gaming accessible to everyone.
            </p>
          </div>
        </div>

        <!-- Quick Links -->
        <div class="md:col-span-2">
          <h3 class="text-text font-semibold text-lg mb-5 relative after:content-[''] after:absolute after:w-10 after:h-1 after:bg-primary after:-bottom-2 after:left-0">Quick Links</h3>
          <nav class="flex flex-col space-y-3">
            <router-link to="/" class="text-text-secondary hover:text-primary transition-colors flex items-center group">
              <span class="w-1 h-1 bg-primary rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Home
            </router-link>
            <router-link to="/about" class="text-text-secondary hover:text-primary transition-colors flex items-center group">
              <span class="w-1 h-1 bg-primary rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity"></span>
              About Us
            </router-link>
            <router-link to="/contact" class="text-text-secondary hover:text-primary transition-colors flex items-center group">
              <span class="w-1 h-1 bg-primary rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Contact Us
            </router-link>
            <router-link to="/faq" class="text-text-secondary hover:text-primary transition-colors flex items-center group">
              <span class="w-1 h-1 bg-primary rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity"></span>
              FAQ
            </router-link>
          </nav>
        </div>

        <!-- Legal -->
        <div class="md:col-span-2">
          <h3 class="text-text font-semibold text-lg mb-5 relative after:content-[''] after:absolute after:w-10 after:h-1 after:bg-primary after:-bottom-2 after:left-0">Legal</h3>
          <nav class="flex flex-col space-y-3">
            <router-link to="/terms" class="text-text-secondary hover:text-primary transition-colors flex items-center group">
              <span class="w-1 h-1 bg-primary rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Terms & Conditions
            </router-link>
            <router-link to="/privacy" class="text-text-secondary hover:text-primary transition-colors flex items-center group">
              <span class="w-1 h-1 bg-primary rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Privacy Policy
            </router-link>
            <router-link to="/refund" class="text-text-secondary hover:text-primary transition-colors flex items-center group">
              <span class="w-1 h-1 bg-primary rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity"></span>
              Refund Policy
            </router-link>
          </nav>
        </div>

        <!-- Newsletter -->
        <div class="md:col-span-4">
          <h3 class="text-text font-semibold text-lg mb-5 relative after:content-[''] after:absolute after:w-10 after:h-1 after:bg-primary after:-bottom-2 after:left-0">Stay Connected</h3>
          <p class="text-text-secondary mb-5">Subscribe to our newsletter for the latest gaming offers and promotions.</p>

          <form @submit.prevent="subscribeNewsletter" class="flex flex-col sm:flex-row gap-2">
            <div class="relative flex-grow">
              <input
                  type="email"
                  placeholder="Your email address"
                  v-model="email"
                  class="w-full px-4 py-3 bg-background border border-background-lighter rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                  required
              >
              <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-text-secondary">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
            </div>

            <button
                type="submit"
                class="bg-primary hover:bg-primary-dark text-white font-medium px-6 py-3 rounded-lg transition-colors flex-shrink-0 flex items-center justify-center"
            >
              Subscribe
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </form>

          <!-- Social Media -->
          <div class="mt-8">
            <h4 class="text-text font-medium mb-4">Follow Us</h4>
            <div class="flex space-x-3">
              <!-- Facebook -->
              <a
                  href="https://www.facebook.com/eskragSS"
                  target="_blank"
                  class="w-10 h-10 rounded-full bg-background hover:bg-[#1877F2] hover:text-white flex items-center justify-center transition-all transform hover:scale-110 hover:shadow-md text-text-secondary"
                  aria-label="Facebook"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill-rule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clip-rule="evenodd"></path>
                </svg>
              </a>

              <!-- Telegram -->
              <a
                  href="https://t.me/esk_rag"
                  target="_blank"
                  class="w-10 h-10 rounded-full bg-background hover:bg-[#0088cc] hover:text-white flex items-center justify-center transition-all transform hover:scale-110 hover:shadow-md text-text-secondary"
                  aria-label="Telegram"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </a>

              <!-- WhatsApp -->
              <a
                  href="https://wa.me/21377572273"
                  target="_blank"
                  class="w-10 h-10 rounded-full bg-background hover:bg-[#25D366] hover:text-white flex items-center justify-center transition-all transform hover:scale-110 hover:shadow-md text-text-secondary"
                  aria-label="WhatsApp"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.017-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                </svg>
              </a>

              <!-- Instagram -->
              <a
                  href="https://instagram.com/esk_rag"
                  target="_blank"
                  class="w-10 h-10 rounded-full bg-background hover:bg-[#E1306C] hover:text-white flex items-center justify-center transition-all transform hover:scale-110 hover:shadow-md text-text-secondary"
                  aria-label="Instagram"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill-rule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clip-rule="evenodd"></path>
                </svg>
              </a>

               <!-- YouTube -->
              <a
                  href="https://www.youtube.com/@esk_rag"
                  target="_blank"
                  class="w-10 h-10 rounded-full bg-background hover:bg-[#FF0000] hover:text-white flex items-center justify-center transition-all transform hover:scale-110 hover:shadow-md text-text-secondary"
                  aria-label="YouTube"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Footer with current date and user -->
    <div class="bg-background border-t border-background-lighter">
      <div class="container mx-auto px-4 py-4">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div class="text-text-secondary text-sm">
            <p>© {{ new Date().getFullYear() }} Eskrag. All rights reserved.</p>
          </div>

          <div class="flex items-center text-sm text-text-secondary">
            <div class="mr-4 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
              </svg>

            </div>
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1 text-primary" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
              </svg>
              <span>{{ currentDate }}</span>
            </div>
          </div>

          <div class="flex space-x-5">
            <a href="#" class="text-text-secondary hover:text-primary text-sm">Cookie Policy</a>
            <a href="#" class="text-text-secondary hover:text-primary text-sm">Sitemap</a>
            <router-link to="/" class="text-text-secondary hover:text-primary text-sm flex items-center">
              Back to Top
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { ref } from 'vue';
import api from '../services/api';

export default {
  name: 'Footer',
  setup() {
    const email = ref('');
    // Updated with exact date and username provided
    const currentDate = ref(new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }));
    const handleImageError = (event) => {
      const parent = event.target.parentNode;
      parent.innerHTML = `<span class="text-white font-bold text-sm">E</span>`;
    };

    const subscribeNewsletter = async () => {
      if (!email.value) return;

      // isLoading.value = true;


      try {
        const response = await api.post('/newsletter/subscribe', {
          email: email.value,
          name: '', // Optional: you can add a name field if needed
          source: 'website' // Track where the subscription came from
        });

        if (response.data.success) {

          alert(`Successfully subscribed to our newsletter!`);

          email.value = ''; // Clear the input
        } else {
          alert('Subscription failed');
        }
      } catch (error) {
        console.error('Subscription error:', error);

        // Handle different error types
        if (error.response?.status === 409) {
          alert('This email is already subscribed.');
        }
         else {
            alert('Failed to subscribe. Please try again later.');


        }
      } finally {
        // isLoading.value = false;
      }
    };
    return {
      email,
      currentDate,
      handleImageError,
      subscribeNewsletter
    };
  }
}
</script>

<style scoped>
/* Add subtle animations and transitions */
@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

/* Hover transitions for links */
a {
  transition: all 0.2s ease;
}

/* Input focus effect - FIXED without using custom property */
input:focus {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2); /* Using a standard blue color with opacity */
}
</style>